import React, { useState } from 'react'
import { supabase } from '../lib/supabase'
import { getPasswordResetRedirectUrl } from '../lib/authRedirects'
import ToggleSwitch from './ToggleSwitch'

const InviteCustomerModal = ({ isOpen, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    email: '',
    businessName: '',
    customMessage: '',
    automationEnabled: false,
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')
    setLoading(true)

    try {
      // Generate a temporary password
      const tempPassword = Math.random().toString(36).slice(-8) + 'A1!'
      
      const { data: currentUser } = await supabase.auth.getUser()

      // Create user in Supabase Auth with email confirmation required
      const { data: authUser, error: authError } = await supabase.auth.admin.createUser({
        email: formData.email,
        password: tempPassword,
        email_confirm: false // They need to confirm via invitation
      })

      if (authError) {
        throw authError
      }

      // Create user record in users table
      const { error: userError } = await supabase
        .from('users')
        .insert({
          id: authUser.user.id,
          email: formData.email,
          role: 'customer'
        })

      if (userError) {
        throw userError
      }

      // Create business record
      const { data: businessRecord, error: businessError } = await supabase
        .from('businesses')
        .insert({
          business_name: formData.businessName,
          owner_email: formData.email,
          admin_id: currentUser?.user?.id,
          status: 'inactive', // Initially inactive until they accept invitation
          invited_at: new Date().toISOString()
        })
        .select('*')
        .single()

      if (businessError) {
        throw businessError
      }

      const automationPayload = {
        business_id: businessRecord.id,
        is_enabled: !!formData.automationEnabled,
        enabled_at: formData.automationEnabled ? new Date().toISOString() : null,
        enabled_by: formData.automationEnabled ? currentUser?.user?.id : null,
      }

      const { error: automationError } = await supabase
        .from('business_automation_profiles')
        .upsert(automationPayload, { onConflict: 'business_id' })

      if (automationError) {
        throw automationError
      }

      // Send invitation email (password reset acts as invitation)
      const passwordResetRedirectTo = getPasswordResetRedirectUrl()
      const { error: inviteError } = await supabase.auth.resetPasswordForEmail(
        formData.email,
        {
          redirectTo: passwordResetRedirectTo
        }
      )

      if (inviteError) {
        console.warn('Failed to send invitation email:', inviteError.message)
        // Don't fail the whole process if email fails
      }

      // Reset form
      setFormData({
        email: '',
        businessName: '',
        customMessage: '',
        automationEnabled: false,
      })

      onSuccess({
        email: formData.email,
        tempPassword,
        emailSent: !inviteError
      })
      onClose()
    } catch (err) {
      setError(err.message || 'Failed to invite customer')
    } finally {
      setLoading(false)
    }
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-screen overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold text-gray-900">Invite New Customer</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            ✕
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-100 border border-red-300 text-red-700 rounded">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700">
              Customer Email Address
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              required
              className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              placeholder="customer@example.com"
            />
          </div>

          <div>
            <label htmlFor="businessName" className="block text-sm font-medium text-gray-700">
              Business Name
            </label>
            <input
              type="text"
              id="businessName"
              name="businessName"
              value={formData.businessName}
              onChange={handleInputChange}
              required
              className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              placeholder="Acme Corporation"
            />
          </div>

          <ToggleSwitch
            id="automationEnabledInvite"
            label="Running Automation?"
            description="Turn on if this invited customer should receive automation services right away."
            checked={formData.automationEnabled}
            onChange={(value) =>
              setFormData((prev) => ({
                ...prev,
                automationEnabled: value,
              }))
            }
          />

          <div>
            <label htmlFor="customMessage" className="block text-sm font-medium text-gray-700">
              Custom Message (Optional)
            </label>
            <textarea
              id="customMessage"
              name="customMessage"
              rows={3}
              value={formData.customMessage}
              onChange={handleInputChange}
              className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              placeholder="Welcome to our financial dashboard platform..."
            />
          </div>

          <div className="bg-blue-50 p-3 rounded-md">
            <p className="text-sm text-blue-800">
              <strong>Note:</strong> The customer will receive an invitation email with instructions to set up their account and password.
            </p>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
            >
              {loading ? 'Sending Invitation...' : 'Send Invitation'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default InviteCustomerModal